import { useState, useEffect, useRef } from "react";
import { Table, Input, Space, Spin, Modal } from "antd";
import { message } from "../../components/antdMessageBridge";
import CommonObj from "../../CommonObj";
import { useNavigate } from "react-router-dom";
import Html5QrcodePlugin from "../qrscanner/qrscanner";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import RecycleIcon from "../../assets/img/svg/recycle.svg";
import "./readytorecycle.css";
import { Html5QrcodeScanType } from "html5-qrcode";
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";
import regexService from "../../components/commonregex/regexService";
import { showManualEntryDisabledWarning } from "../../components/commonvalidation/manualEntryWarning";
import useFieldPermissions from "../../hooks/useFieldPermissions";

const ReadyTorecycleConfirmation = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [regexLoaded, setRegexLoaded] = useState(false);
    const { isReadOnly } = useFieldPermissions("readytorecycle");

    const [form, setForm] = useState({
        bid: "",
        title: "",
        author: "",
        publisher: "",
        isbN10: "",
        isbN13: "",
        bookPageCount: "",
        language: "",
        weight: "0.75",
        isbn: "",
        condition: "",
        content: "",
        bookFormat: "",
        eqComplexityLevel: "",
        rackAndShelf: "1",
        comments: "",
    });

    const { accessToken } = useAuthToken();
    const [cusdectext, setCusDecText] = useState("");
    const [scanimage, setScanImage] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    const [activeScanner, setActiveScanner] = useState(null);
    const isProcessingRef = useRef(false);
    const lastBidKeyTimeRef = useRef(0);
    const clearBidTimerRef = useRef(null);

    useEffect(() => {
        return () => {
            clearTimeout(clearBidTimerRef.current);
        };
    }, []);

    useEffect(() => {
        if (scanimage && cusdectext.trim() !== "") {
            BarcodeScan();
            setScanImage(false);
            setActiveScanner(null);
            setCusDecText("");
        }
    }, [scanimage, cusdectext]);

    const onNewScanResult = (decodedText) => {
        if (isProcessingRef.current) return;
        isProcessingRef.current = true;
        setCusDecText(decodedText);
        setForm({ ...form, bid: decodedText });
        setScanImage(true);
    };

    useEffect(() => {
        sessionStorage.removeItem("selectedItems");
        sessionStorage.removeItem("showIcons");
        const loadRegexPatterns = async () => {
            try {
                await regexService.fetchRegexData(accessToken);
                setRegexLoaded(true);
            } catch (error) {
                console.error("Failed to load regex patterns:", error);
            }
        };

        if (accessToken) {
            loadRegexPatterns();
        }
    }, [accessToken]);

    const handleEdit = (key) => {
        navigate("/recycle-bid", { state: { key } });
    };

    const columns = [
        {
            title: "Rack ID",
            dataIndex: "rack",
            key: "rack",
            width: 200,
            render: (_, record) => (
                <Space size="middle">
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            handleEdit(record.rack);
                        }}
                        style={{ textDecoration: "none" }}
                    >
                        {record.rack}
                    </a>
                </Space>
            ),
        },
        {
            title: "Total Books",
            dataIndex: "totalBooks",
            key: "totalBooks",
            width: 160,
        },
        {
            title: "Approved",
            dataIndex: "approved",
            key: "approved",
            width: 200,
        },
        {
            title: "Rejected",
            dataIndex: "rejected",
            key: "rejected",
            width: 120,
        },
        {
            title: "Review Pending",
            dataIndex: "reviewPending",
            key: "reviewPending",
            width: 200,
        },
        {
            title: "Other Status",
            dataIndex: "otherStatus",
            key: "otherStatus",
            width: 200,
        },
    ];

    const getRowClassName = (_, index) => {
        return index % 2 === 0 ? "odd-row" : "even-row";
    };

    const handleTableChange = (newPagination) => {
        setPagination((prev) => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));

        fetchData();
    };

    const fetchData = async () => {
        const bidValue = form?.bid?.trim() || "";
        if (bidValue.length > 0) {
            const payload = {
                rack: form?.bid || "",
            };
            setLoading(true);
            try {
                const myHeaders = {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                    "X-CSRF-Token": encryptTimestamp(),
                };
                const response = await fetch(
                    CommonObj.Endpoints.GetBIDConfirmationByRack,
                    {
                        method: "POST",
                        headers: myHeaders,
                        body: JSON.stringify(payload),
                    }
                );

                if (!response.ok) {
                    throw new Error("Network response was not ok");
                }
                const data = await response.json();
                console.log("resulserach???", data);

                if (Array.isArray(data) && data.length === 0) {
                    setData([]);
                    setPagination((prev) => ({ ...prev, total: 0 }));
                    setForm((prev) => ({ ...prev, bid: "" }));
                    message.error("Invalid Rack ID");
                    return;
                }

                if (data?.error === "No Ready to Recycle Details found") {
                    setData([]);
                    message.warning("The scanned Rack ID is not exist");
                } else {
                    setData(data);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        } else {
            setData([]);
            setPagination((prev) => ({
                ...prev,
                current: 1,
                total: 0,
            }));
        }
    };

    const BarcodeScan = async () => {
        if (!scanimage || cusdectext.trim() === "") return;
        setScanImage(false);
        try {
            await cusSearch(true);
        } finally {
            isProcessingRef.current = false;
        }
    };

    const handleEmptyBid = () => {
        setData([]);
        setPagination((prev) => ({
            ...prev,
            current: 1,
            total: 0,
        }));
    };

    const fetchBIDConfirmation = async (bidValue) => {
        const payload = { rack: bidValue };

        try {
            const response = await makeBIDRequest(payload);
            const data = await response.json();

            handleBIDResponse(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const makeBIDRequest = async (payload) => {
        const myHeaders = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            "X-CSRF-Token": encryptTimestamp(),
        };

        const response = await fetch(CommonObj.Endpoints.GetBIDConfirmationByRack, {
            method: "POST",
            headers: myHeaders,
            body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error("Network response was not ok");
        return response;
    };

    const handleBIDResponse = (data) => {
        if (isEmptyArray(data)) {
            handleEmptyArrayResponse();
            return;
        }

        if (data?.error === "No Ready to Recycle Details found") {
            handleNoRecycleDetails();
        } else {
            handleValidData(data);
        }
    };

    const isEmptyArray = (data) => Array.isArray(data) && data.length === 0;

    const handleEmptyArrayResponse = () => {
        setData([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        message.error("Invalid Rack ID");
    };

    const handleNoRecycleDetails = () => {
        setData([]);
        message.destroy();
        message.warning({
            content: "The scanned Rack ID does not exist",
            key: "rackIdWarning",
            duration: 3,
        });
    };

    const handleValidData = (data) => {
        setData((prevData) => [...prevData, ...data]);
        const total = data[0]?.totalRows || 0;
        setPagination((prev) => ({ ...prev, total }));
        setForm((prev) => ({ ...prev, bid: "" }));
    };

    const cusSearch = async (isScannerFlow = false) => {
        if (isReadOnly("rack") && !isScannerFlow) {
            showManualEntryDisabledWarning();
            return;
        }

        const bidValue = form?.bid?.trim() || "";

        if (bidValue.length === 0) return;

        if (regexLoaded && !(await validateRackFormat(bidValue))) {
            handleInvalidFormat(bidValue);
            return;
        }

        if (await hasDuplicateRack(bidValue)) {
            handleDuplicateError();
            return;
        }

        await fetchRackConfirmation(bidValue);
    };

    const hasDuplicateRack = async (bidValue) => {
        const standardizedInput = regexService.formatStandardRack(bidValue);

        return data.some((item) => {
            const standardizedGridValue = regexService.formatStandardRack(item.rack);

            return standardizedGridValue?.toString().toLowerCase() === standardizedInput?.toString().toLowerCase();
        });
    };

    const validateRackFormat = async (bidValue) => {
        const validation = regexService.validateRack(bidValue);
        return validation.isValid;
    };

    const handleDuplicateError = () => {
        message.error({ content: "Rack Number already exists in the grid", key: "rackDuplicate" });
        setForm({ bid: "" });
    };

    const handleInvalidFormat = (bidValue) => {
        setForm({ bid: "" });
        message.error({ content: "Input format invalid: No matching pattern found.", key: "rackInvalidFormat" });
    };

    const fetchRackConfirmation = async (bidValue) => {
        setLoading(true);

        try {
            const responseData = await apiPostRackSearch({ rack: bidValue });

            if (isEmptyResult(responseData)) {
                handleEmptyResult();
                return;
            }

            handleApiResponse(responseData);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const apiPostRackSearch = async (payload) => {
        const myHeaders = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            "X-CSRF-Token": encryptTimestamp(),
        };

        const response = await fetch(CommonObj.Endpoints.GetBIDConfirmationByRack, {
            method: "POST",
            headers: myHeaders,
            body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error("Network response was not ok");

        return response.json();
    };

    const isEmptyResult = (responseData) =>
        Array.isArray(responseData) && responseData.length === 0;

    const handleEmptyResult = () => {
        setForm((prev) => ({ ...prev, bid: "" }));
        message.error("Invalid Rack ID");
    };

    const handleApiResponse = (responseData) => {
        if (responseData?.error === "No Ready to Recycle Details found") {
            message.warning("The scanned Rack ID does not exist");
        } else {
            setData((prevData) => [...prevData, ...responseData]);
            setForm({ bid: "" });
            setPagination((prev) => ({
                ...prev,
                total: prev.total + responseData.length,
            }));
        }
    };

    if (loading) {
        return (
            <Spin size="large" style={{ margin: "20px auto" }} />
        );
    }

    return (
        <>
            <Modal
                title={`Scan ${activeScanner ? activeScanner : ""}`}
                open={!!activeScanner}
                onCancel={() => setActiveScanner(null)}
                footer={null}
                destroyOnClose
                className="scan-model-container"
                width={1000}
            >
                <div className="scan-modal">
                    <Html5QrcodePlugin
                        fps={30}
                        qrbox={{ width: 1000, height: 400 }}
                        aspectRatio={2.8}
                        disableFlip={false}
                        qrCodeSuccessCallback={onNewScanResult}
                        supportedScanTypes={[Html5QrcodeScanType.SCAN_TYPE_CAMERA]}
                    />
                </div>
            </Modal>
            <main className="book-page-main-container ready-to-recylce-container">
                <div className="book-page-container">
                    <div className="book-page-title-container no-padding-right">
                        <div className="book-page-title">
                            <span>
                                <img src={RecycleIcon} alt="Recylce icon"></img>
                            </span>
                            <h2>Ready To Recycle</h2>
                        </div>
                    </div>
                    <div className="book-page-form">
                        <div className="book-page-camera-button-container multiple">
                            <Input
                                placeholder=""
                                value={form.bid}
                                className=""
                                onChange={(e) => {
                                    setForm({ ...form, bid: e.target.value });
                                }}
                                onKeyDown={(e) => {
                                    if (!isReadOnly("rack")) {
                                        return;
                                    }

                                    // Let native copy/paste behave normally (no custom clipboard handling).
                                    const isCopyOrPasteShortcut =
                                        (e.ctrlKey || e.metaKey) && ["c", "v", "C", "V"].includes(e.key);
                                    if (isCopyOrPasteShortcut) {
                                        return;
                                    }

                                    if (e.key === 'Enter' || e.key === 'Tab') {
                                        return;
                                    }

                                    const currentTime = Date.now();
                                    const timeDiff = currentTime - lastBidKeyTimeRef.current;
                                    lastBidKeyTimeRef.current = currentTime;

                                    // A physical scanner sends characters within a few milliseconds of each
                                    // other, so only block keystrokes that arrive slowly (manual typing).
                                    // The very first keystroke (field currently empty) is always let through
                                    // so a scan burst never loses its first character.
                                    const isSlowManualKeystroke = timeDiff > 50 && e.currentTarget.value.length > 0;
                                    if (!isSlowManualKeystroke) {
                                        return;
                                    }

                                    e.preventDefault();
                                    setForm((prev) => ({ ...prev, bid: '' }));

                                    clearTimeout(clearBidTimerRef.current);
                                    clearBidTimerRef.current = setTimeout(() => {
                                        showManualEntryDisabledWarning();
                                    }, 300);
                                }}
                                onPressEnter={(e) => {
                                    if (isReadOnly("rack")) {
                                        e.preventDefault();
                                        cusSearch(true);
                                        return;
                                    }
                                    cusSearch();
                                }}
                            />
                            {form.bid.length === 0 && (
                                <label>
                                    {isReadOnly("rack") ? "Scan " : "Enter / Scan "}Rack Number Using “
                                    <strong>Camera Icon</strong>”
                                    <span className="form-required">*</span>
                                </label>
                            )}
                            <button
                                type="button"
                                className="book-page-camera-button"
                                data-track="ReadyToRecycle-open-rack-scanner"
                                onClick={() => setActiveScanner("Rack")}
                            >
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        cx="12"
                                        cy="13"
                                        r="3"
                                        stroke="#1C274C"
                                        strokeWidth="1.5"
                                    />
                                    <path
                                        opacity="0.5"
                                        d="M10 19.9999H14C16.8089 19.9999 18.2134 19.9999 19.2223 19.3258C19.659 19.0339 20.034 18.6589 20.3259 18.2222C21 17.2133 21 15.8088 21 12.9999C21 10.191 20.9999 8.78673 20.3257 7.77783C20.0339 7.34107 19.6589 6.96607 19.2221 6.67423C18.2132 6.00011 16.8088 6.00011 13.9999 6.00011H9.99985C7.19093 6.00011 5.78646 6.00011 4.77757 6.67423C4.34081 6.96607 3.96581 7.34107 3.67397 7.77783C3 8.7865 3 10.1903 3 12.998L3 12.9999C3 15.8088 3 17.2133 3.67412 18.2222C3.96596 18.6589 4.34096 19.0339 4.77772 19.3258C5.78661 19.9999 7.19108 19.9999 10 19.9999Z"
                                        stroke="#1C274C"
                                        strokeWidth="1.5"
                                    />
                                    <path
                                        d="M18 10H17.5"
                                        stroke="#1C274C"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                    />
                                    <path
                                        opacity="0.5"
                                        d="M14.5 3.5H9.5"
                                        stroke="#1C274C"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                    />
                                </svg>
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M8.00098 4C8.00098 3.44772 8.41425 3 8.92405 3H15.0779C15.5877 3 16.001 3.44772 16.001 4C16.001 4.55228 15.5877 5 15.0779 5H8.92405C8.41425 5 8.00098 4.55228 8.00098 4Z"
                                        fill="url(#paint0_linear_273_6917)"
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M9.77875 21H14.2232C17.3442 21 18.9047 21 20.0257 20.2777C20.511 19.965 20.9277 19.5632 21.252 19.0953C22.001 18.0143 22.001 16.5095 22.001 13.4999C22.001 10.4903 22.0008 8.9857 21.2518 7.90473C20.9275 7.43676 20.5109 7.03497 20.0256 6.72228C18.9046 6 17.3441 6 14.223 6H9.77859C6.65756 6 5.09705 6 3.97605 6.72228C3.49077 7.03497 3.0741 7.43676 2.74984 7.90473C2.00098 8.98547 2.00098 10.4896 2.00098 13.4979L2.00098 13.4999C2.00098 16.5095 2.00098 18.0143 2.75 19.0953C3.07426 19.5632 3.49093 19.965 3.97622 20.2777C5.09721 21 6.65773 21 9.77875 21ZM7.83431 13.4999C7.83431 11.2808 9.69979 9.48196 12.001 9.48196C14.3022 9.48196 16.1676 11.2808 16.1676 13.4999C16.1676 15.7189 14.3022 17.5178 12.001 17.5178C9.69979 17.5178 7.83431 15.7189 7.83431 13.4999ZM9.50098 13.4999C9.50098 12.1685 10.6203 11.0891 12.001 11.0891C13.3817 11.0891 14.501 12.1685 14.501 13.4999C14.501 14.8313 13.3817 15.9106 12.001 15.9106C10.6203 15.9106 9.50098 14.8313 9.50098 13.4999ZM18.1121 9.48196C17.6519 9.48196 17.2788 9.84174 17.2788 10.2855C17.2788 10.7294 17.6519 11.0891 18.1121 11.0891H18.6676C19.1279 11.0891 19.501 10.7294 19.501 10.2855C19.501 9.84174 19.1279 9.48196 18.6676 9.48196H18.1121Z"
                                        fill="url(#paint1_linear_273_6917)"
                                    />
                                    <defs>
                                        <linearGradient
                                            id="paint0_linear_273_6917"
                                            x1="22.601"
                                            y1="11.9503"
                                            x2="1.98296"
                                            y2="14.3301"
                                            gradientUnits="userSpaceOnUse"
                                        >
                                            <stop stopColor="#033D9B" />
                                            <stop offset="1" stopColor="#29ACD5" />
                                        </linearGradient>
                                        <linearGradient
                                            id="paint1_linear_273_6917"
                                            x1="22.601"
                                            y1="11.9503"
                                            x2="1.98296"
                                            y2="14.3301"
                                            gradientUnits="userSpaceOnUse"
                                        >
                                            <stop stopColor="#033D9B" />
                                            <stop offset="1" stopColor="#29ACD5" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </button>
                            <button
                                type="button"
                                className="book-page-camera-button-input-2"
                                onClick={() => cusSearch(true)}
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10ZM11.0303 6.46967C10.7374 6.17678 10.2626 6.17678 9.96967 6.46967C9.67678 6.76256 9.67678 7.23744 9.96967 7.53033L12.4393 10L9.96967 12.4697C9.67678 12.7626 9.67678 13.2374 9.96967 13.5303C10.2626 13.8232 10.7374 13.8232 11.0303 13.5303L14.0303 10.5303C14.3232 10.2374 14.3232 9.76256 14.0303 9.46967L11.0303 6.46967ZM5.96967 6.46967C6.26256 6.17678 6.73744 6.17678 7.03033 6.46967L10.0303 9.46967C10.3232 9.76256 10.3232 10.2374 10.0303 10.5303L7.03033 13.5303C6.73744 13.8232 6.26256 13.8232 5.96967 13.5303C5.67678 13.2374 5.67678 12.7626 5.96967 12.4697L8.43934 10L5.96967 7.53033C5.67678 7.23744 5.67678 6.76256 5.96967 6.46967Z"
                                        fill="url(#paint0_linear_1900_8687)"
                                    />
                                    <defs>
                                        <linearGradient
                                            id="paint0_linear_1900_8687"
                                            x1="20.6"
                                            y1="9.94475"
                                            x2="-0.0696504"
                                            y2="12.0919"
                                            gradientUnits="userSpaceOnUse"
                                        >
                                            <stop stopColor="#033D9B" />
                                            <stop offset="1" stopColor="#29ACD5" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div className="active-page-container">
                        <div className="active-page-body-container">
                            <div className="rounded-table-wrapper">
                                <Table
                                    rowSelection={false}
                                    columns={columns}
                                    loading={loading}
                                    dataSource={data}
                                    className="common-grid"
                                    pagination={false}
                                    rowKey="bid"
                                    scroll={{ x: "max-content", y: "calc(100vh - 280px)" }}
                                    rowClassName={getRowClassName}
                                    onChange={handleTableChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};
export default ReadyTorecycleConfirmation;